'use client'

import React, { forwardRef, useImperativeHandle, useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { cn, testRideFormSchema, useMediaQuery, z } from '@segway/core'
import { addYears, endOfYear, format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { useForm, UseFormReturn } from 'react-hook-form'

import { Select } from '@/components'
import {
  Calendar,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/ui'

export interface TestRideFormRef {
  form: UseFormReturn<z.infer<typeof testRideFormSchema>>
}

/**
 * 试驾信息表单
 */
const TestRideForm = forwardRef<TestRideFormRef>((_, ref) => {
  const responsive = useMediaQuery()

  const isMobile = responsive && !responsive.xl

  const form = useForm<z.infer<typeof testRideFormSchema>>({
    resolver: zodResolver(testRideFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      time: '',
    },
  })

  /**
   * 时间选项
   */
  const timeOptions = useMemo(() => {
    const intervals = []
    let startHour = 8
    while (startHour < 20) {
      const endHour = startHour + 2
      const startHourStr = startHour.toString().padStart(2, '0')
      const endHourStr = endHour.toString().padStart(2, '0')
      intervals.push({
        label: `${startHourStr}:00-${endHourStr}:00`,
        value: `${startHourStr}:00`,
      })
      startHour = endHour
    }
    return intervals
  }, [])

  useImperativeHandle(ref, () => ({
    form,
  }))

  const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 300)
  }

  return (
    <>
      <h3 className="mb-[20px] mt-[48px] font-miSansDemiBold450 text-[20px] xl:mt-9 xl:text-2xl">
        Customer Information
      </h3>
      <Form {...form}>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 flex">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem className="w-[50%] xl:w-[60%]">
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <button
                          className={cn(
                            'flex h-[48px] w-full items-center rounded-[8px] rounded-r-none border border-r-0 border-gray-2 px-4 font-miSansMedium380 text-base text-gray-4',
                            field.value && 'text-gray-8',
                          )}>
                          {field.value ? (
                            format(field.value, 'yyyy-MM-dd')
                          ) : (
                            <span>Preferred Date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        className="w-full"
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date < new Date() || date > endOfYear(addYears(new Date(), 1))
                        }
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="time"
              render={({ field }) => (
                <FormItem className="w-[50%] xl:w-[40%]">
                  <FormControl>
                    <Select
                      placeholder="Preferred time"
                      className={cn(
                        'flex h-[48px] w-full touch-manipulation items-center rounded-[8px] rounded-l-none border border-gray-2 px-4 font-miSansMedium380 text-base text-gray-4',
                        field.value && 'text-gray-8',
                      )}
                      contentClassName={cn(
                        responsive && !responsive.xl ? 'max-h-[200px] overflow-y-auto' : '',
                      )}
                      options={timeOptions}
                      onValueChange={field.onChange}
                      value={field.value}
                      itemClassName="h-[54px] text-[14px] leading-[54px] bg-transparent rounded-none whitespace-nowrap overflow-hidden text-ellipsis text-gray-5 hover:text-black border-b-2 border-solid border-gray-1"
                      type="noBorder"></Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="h-[48px]"
                    onFocus={(event) => handleFocus(event)}
                    label="First name"
                    required
                    {...field}
                    error={!!form.formState.errors.firstName}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Last name"
                    className="h-[48px]"
                    onFocus={(event) => handleFocus(event)}
                    required
                    {...field}
                    error={!!form.formState.errors.lastName}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className={isMobile ? 'col-span-2' : ''}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      label="Email address"
                      className="h-[48px]"
                      onFocus={(event) => handleFocus(event)}
                      required
                      {...field}
                      error={!!form.formState.errors.email}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className={isMobile ? 'col-span-2' : ''}>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      label="Phone number"
                      className="h-[48px]"
                      onFocus={(event) => handleFocus(event)}
                      required
                      {...field}
                      error={!!form.formState.errors.phone}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </Form>
    </>
  )
})

TestRideForm.displayName = 'TestRideForm'

export default TestRideForm
